/* eslint-disable @typescript-eslint/return-await,  @typescript-eslint/no-explicit-any */
import { ModelsOutgoingMessage, OutgoingMessageChatMessage } from '@engyalo/salesdesk-api-ts-client';
import i18n from 'i18n';
import { jsPDF } from 'jspdf';
import { logError, logWarn } from 'services/Logger';
import { convertDateToSeparator, convertSeparatorToDate, formatDate, formatMessageTime } from 'helpers/formatterDates';

enum AUTHOR_ROLES {
  USER = 'user',
  BOT = 'bot',
  AGENT = 'agent',
}

enum MESSAGE_KINDS {
  CHAT = 'chat',
  STATUS = 'status',
}

enum MESSAGES_STATUS {
  DEPARTMENT_TRANSFER = 'department-transfer',
}

// Types taken from the API location is not available at the moment
enum MESSAGE_CONTENT_TYPES {
  TEXT = 'text',
  IMAGE = 'image',
  AUDIO = 'audio',
  INTERACTIVE = 'interactive',
  USER_REPLY = 'userReply',
  BUTTON = 'button',
  DOCUMENT = 'document',
  VIDEO = 'video',
  LIST = 'list',
}

const font = 'helvetica';
const normal = 'normal';
const bold = 'bold';
const italic = 'italic';

/**
 * Helper function to fetch an image and convert it to base64
 */
const getImageAsBase64 = async (imageUrl: string): Promise<string | null> => {
  try {
    // Add a cache-busting parameter to avoid CORS issues with some image hosts
    const urlWithParam = new URL(imageUrl);
    urlWithParam.searchParams.append('_t', Date.now().toString());

    const response = await fetch(urlWithParam.toString(), {
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        Accept: 'image/jpeg, image/png, image/webp, image/*',
      },
    });

    if (!response.ok) {
      logWarn(`Failed to fetch image: ${response.status} ${response.statusText}`);
      return null;
    }

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    logWarn('Error loading image:', error);
    return null;
  }
};

/**
 * Function to replace emojis with text descriptions
 */
const replaceEmojis = (text: string): string => {
  if (!text) return '';

  // Replace common emojis with text descriptions using simple ASCII characters
  return (
    text
      // Transportation
      .replace(/🚚/g, '[truck]')
      .replace(/🚎/g, '[bus]')
      .replace(/🚗/g, '[car]')
      .replace(/✈️/g, '[airplane]')

      // Objects
      .replace(/🔊/g, '[audio]')
      .replace(/⏳/g, '[hourglass]')
      .replace(/📱/g, '[mobile]')
      .replace(/📞/g, '[phone]')
      .replace(/📧/g, '[email]')
      .replace(/📩/g, '[message]')
      .replace(/📘/g, '[book]')
      .replace(/📚/g, '[books]')
      .replace(/🛍️/g, '[shopping]')
      .replace(/🎓/g, '[graduation]')
      .replace(/🔄/g, '[refresh]')
      .replace(/⚙️/g, '[settings]')

      // Faces & People
      .replace(/☺️/g, '[smile]')
      .replace(/😀/g, '[smile]')
      .replace(/😊/g, '[smile]')
      .replace(/🧑‍💻/g, '[agent]')

      // Hand gestures
      .replace(/👍/g, '[thumbs up]')
      .replace(/👋/g, '[wave]')
      .replace(/👇/g, '[pointing down]')

      // Symbols
      .replace(/◉/g, '•')
      .replace(/◯/g, 'o')
      .replace(/⚠️/g, '[warning]')
      .replace(/✅/g, '[check]')
      .replace(/❌/g, '[x]')
      .replace(/✓/g, '[check]')
      .replace(/✗/g, '[x]')
      .replace(/→/g, '[right arrow]')
      .replace(/←/g, '[left arrow]')
      .replace(/↑/g, '[up arrow]')
      .replace(/↓/g, '[down arrow]')
  );
};

/**
 * Helper function to check if enough space is available on current page
 * and add a new page if needed
 */
const checkPageBreak = (
  pdf: jsPDF,
  currentY: number,
  requiredHeight: number,
  pageHeight: number,
  margin: number,
  footerMargin: number
): number => {
  // If less than requiredHeight + footerMargin space available, add new page
  if (currentY + requiredHeight > pageHeight - (margin + footerMargin)) {
    pdf.addPage();
    return margin + 10; // Return new Y position at top of page with margin
  }
  return currentY;
};

/**
 * Renders text with proper page breaks
 */
function renderTextWithPageBreaks(
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  pageWidth: number,
  pageHeight: number,
  margin: number,
  footerMargin: number
): number {
  if (!text) return y;

  // Process emojis
  const processedText = replaceEmojis(text);

  // Calculate content width
  const contentWidth = pageWidth - margin * 2;

  // Split text into lines that fit the content width
  const textLines = pdf.splitTextToSize(processedText, contentWidth - (x - margin));

  // Calculate total height needed for this text block
  const lineHeight = 5; // Increased line height to avoid cut-off
  const totalTextHeight = textLines.length * lineHeight;

  // Check for page break
  const yAxis = checkPageBreak(pdf, y, totalTextHeight, pageHeight, margin, footerMargin);

  // Now render the text
  pdf.text(textLines, x, yAxis);

  // Return the new Y position
  return y + totalTextHeight;
}

const getContentData = (chat: OutgoingMessageChatMessage, t: any) => {
  const { kind } = chat;
  if (kind === MESSAGE_CONTENT_TYPES.AUDIO) {
    return {
      contentTitle: t('audioMessage'),
      contentUrl: chat.audio?.url || '',
      contentUrlText: t('audioUrl'),
    };
  }
  if (kind === MESSAGE_CONTENT_TYPES.DOCUMENT) {
    return {
      contentTitle: t('documentMessage'),
      contentUrl: chat.document?.url || '',
      contentUrlText: t('documentUrl'),
    };
  }
  if (kind === MESSAGE_CONTENT_TYPES.VIDEO) {
    return {
      contentTitle: t('videoMessage'),
      contentUrl: chat.video?.url || '',
      contentUrlText: t('videoUrl'),
    };
  }
  return null;
};

/**
 * Creates a formatted chat transcript PDF from the messages
 */
export async function exportChatToPdf(
  messages: ModelsOutgoingMessage[],
  instance: string,
  t: any,
  title = 'chatTranscript'
): Promise<void> {
  // Create PDF instance (A4 format)
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    putOnlyUsedFonts: true,
    compress: true,
  });

  // Use standard fonts for better compatibility
  pdf.setFont(font, normal);
  // PDF dimensions
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  const maxImageWidth = contentWidth - 10; // Max width for images
  const maxImageHeight = 60; // Reduced slightly for better page breaks
  const footerMargin = 10; // Space reserved at bottom of page

  // Add title header
  pdf.setFontSize(20);
  pdf.setTextColor(33, 33, 33);
  pdf.text(`${t('chatTranscript')}`, margin, margin + 7);

  // Extract information for header
  let userName = '';
  const agentNames = new Set<string>();
  let earliestTimestamp = '';
  let latestTimestamp = '';

  // Find all unique agent names and user name
  messages.forEach((message) => {
    if (message.kind === MESSAGE_KINDS.CHAT) {
      // Collect agent names
      if (message?.chat?.authorRole === AUTHOR_ROLES.AGENT) {
        agentNames.add(message.chat.name || t('unknownAgent'));
      }

      // Get user name (from the first user message we find)
      if (message?.chat?.authorRole === AUTHOR_ROLES.USER && !userName) {
        userName = message?.chat?.name || t('unknownUser');
      }

      // Track timestamps for conversation period
      const timestamp = message?.chat?.timestamp || '';
      if (!earliestTimestamp || Number(timestamp) < Number(earliestTimestamp)) {
        earliestTimestamp = timestamp;
      }
      if (!latestTimestamp || Number(timestamp) > Number(latestTimestamp)) {
        latestTimestamp = timestamp;
      }
    }
  });

  // Format agent names list
  const agentList = Array.from(agentNames).join(', ');

  // Add metadata fields
  pdf.setFontSize(10);
  pdf.setTextColor(70, 70, 70);

  // User name
  pdf.text(`${t('user')}: ${userName || 'N/A'}`, margin, margin + 22);

  // Agents
  pdf.text(`${t('agent')}(s): ${agentList || 'N/A'}`, margin, margin + 29);

  // Line 4: Instance
  pdf.text(`${instance}`, margin, margin + 36);

  const dateSeparator = convertDateToSeparator(Number(messages[0]?.chat?.timestamp || ''));
  const formattedDate = formatDate(convertSeparatorToDate(dateSeparator), i18n.language);
  const formattedTime = formatMessageTime(Number(messages[0]?.chat?.timestamp || ''), i18n.language);

  pdf.text(`${t('date')}: ${formattedDate} ${t('time')}: ${formattedTime}`, margin, margin + 43);

  // Add separator line
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, margin + 50, pageWidth - margin, margin + 50);

  let yPosition = margin + 60;

  for (const message of messages) {
    if (message.kind === MESSAGE_KINDS.CHAT) {
      const { chat } = message;
      const name =
        chat?.name ||
        (chat?.authorRole === AUTHOR_ROLES.BOT
          ? t('bot')
          : chat?.authorRole === AUTHOR_ROLES.AGENT
          ? t('agent')
          : t('unknown'));
      const time = formatMessageTime(Number(chat?.timestamp || ''), i18n.language);
      const isUser = chat?.authorRole === AUTHOR_ROLES.USER;
      // Check for page break before adding a new message header
      // We need at least 30mm for a message (conservative estimate)
      if (yPosition > pageHeight - (margin + footerMargin + 30)) {
        pdf.addPage();
        yPosition = margin + 10;
      }
      // Set proper text color based on role
      if (isUser) {
        pdf.setTextColor(50, 50, 50);
      } else if (chat?.authorRole === AUTHOR_ROLES.BOT) {
        pdf.setTextColor(0, 67, 166); // Primary color for bot
      } else {
        pdf.setTextColor(39, 174, 96); // Green for agent
      }
      // Author name and timestamp
      pdf.setFontSize(11);
      pdf.setFont(font, bold);
      pdf.text(`${name}`, margin, yPosition);

      pdf.setFont(font, normal);
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`${time}`, pageWidth - margin - 20, yPosition);
      yPosition += 6;

      // Reset color for message content
      pdf.setTextColor(50, 50, 50);
      pdf.setFontSize(10);
      switch (chat?.kind) {
        case MESSAGE_CONTENT_TYPES.TEXT: {
          if (chat.text?.body) {
            // Use our page break aware text rendering
            yPosition = renderTextWithPageBreaks(
              pdf,
              chat.text.body,
              margin,
              yPosition,
              pageWidth,
              pageHeight,
              margin,
              footerMargin
            );
            // Add spacing after text
            yPosition += 5;
          }
          break;
        }
        case MESSAGE_CONTENT_TYPES.IMAGE: {
          if (chat.image?.url) {
            try {
              // Check if we need a new page for the image
              // Images take up a lot of space, so check if we have enough room
              const estimatedImageHeight = maxImageHeight + 20; // Image height plus some extra space

              yPosition = checkPageBreak(pdf, yPosition, estimatedImageHeight, pageHeight, margin, footerMargin);

              // Try to fetch and add the actual image
              let imageBase64 = null;
              try {
                imageBase64 = await getImageAsBase64(chat.image.url);
              } catch (fetchError) {
                logWarn('Failed to load image:', fetchError);
                // Handle the error here - we'll display image URL as text instead
              }

              // Only proceed with image rendering if we got a valid base64 string
              if (imageBase64 && typeof imageBase64 === 'string' && imageBase64.startsWith('data:image')) {
                try {
                  // Create temporary image to get dimensions
                  const img = new Image();
                  img.src = imageBase64;

                  // Calculate dimensions to preserve aspect ratio
                  const aspectRatio = img.width / img.height;

                  // Default to a reasonable aspect ratio if dimensions not available
                  const fallbackAspectRatio = 1.5; // Typical 3:2 aspect ratio
                  const effectiveAspectRatio =
                    isNaN(aspectRatio) || !isFinite(aspectRatio) ? fallbackAspectRatio : aspectRatio;

                  // Calculate dimensions while preserving aspect ratio
                  let imgWidth = maxImageWidth;
                  let imgHeight = imgWidth / effectiveAspectRatio;

                  // If the height would be too large, constrain by height instead
                  if (imgHeight > maxImageHeight) {
                    imgHeight = maxImageHeight;
                    imgWidth = imgHeight * effectiveAspectRatio;
                  }

                  // Add the image to PDF with proper dimensions
                  pdf.addImage(
                    imageBase64,
                    'JPEG',
                    margin,
                    yPosition,
                    imgWidth,
                    imgHeight,
                    undefined, // alias
                    'FAST' // compression: better image quality
                  );

                  // Update position
                  yPosition += imgHeight + 5;
                } catch (error) {
                  logError('Error handling image in PDF:', error);

                  // Fallback to fixed dimensions if there's an issue
                  const imgWidth = maxImageWidth * 0.8;
                  const imgHeight = imgWidth * 0.75; // 4:3 aspect ratio

                  pdf.addImage(imageBase64, 'JPEG', margin, yPosition, imgWidth, imgHeight, undefined, 'FAST');

                  yPosition += imgHeight + 5;
                }
              } else {
                // If image can't be loaded, display a more descriptive fallback
                pdf.setFontSize(9);
                pdf.setTextColor(50, 50, 50);
                pdf.text(`[${t('imageCouldNotBeLoaded')}]`, margin, yPosition);
                yPosition += 5;

                // Check for page break before adding URL
                yPosition = checkPageBreak(pdf, yPosition, 10, pageHeight, margin, footerMargin);

                pdf.setFontSize(8);
                pdf.setTextColor(0, 0, 255);

                // Handle long URLs by breaking them across multiple lines if needed
                const urlText = chat.image.url;
                const urlLines = pdf.splitTextToSize(urlText, contentWidth - 10);

                // Add first line with URL link
                pdf.textWithLink(urlLines[0], margin, yPosition, {
                  url: chat.image.url,
                });
                yPosition += 5;

                // If URL was split into multiple lines, show remaining lines
                if (urlLines.length > 1) {
                  for (let i = 1; i < urlLines.length; i++) {
                    pdf.textWithLink(urlLines[i], margin, yPosition, {
                      url: chat.image.url,
                    });
                    yPosition += 5;
                  }
                }

                // Add extra space after the URL
                yPosition += 3;
              }

              // Add caption if available - with page break check
              if (chat.image.caption) {
                pdf.setFontSize(8);
                pdf.setTextColor(100, 100, 100);

                yPosition = renderTextWithPageBreaks(
                  pdf,
                  `${t('caption')}: ${chat.image.caption}`,
                  margin,
                  yPosition,
                  pageWidth,
                  pageHeight,
                  margin,
                  footerMargin
                );
              }
            } catch (error) {
              logWarn('Error adding image to PDF:', error);
              pdf.setTextColor(50, 50, 50);
              pdf.text(`[${t('image')}]`, margin, yPosition);
              yPosition += 5;

              // Check for page break before adding URL
              yPosition = checkPageBreak(pdf, yPosition, 10, pageHeight, margin, footerMargin);

              pdf.setFontSize(8);
              pdf.setTextColor(0, 0, 255);
              pdf.textWithLink(chat.image.url, margin, yPosition, {
                url: chat.image.url,
              });
              yPosition += 5;
            }
          } else {
            pdf.text(`[${t('imageMessageWithoutUrl')}]`, margin, yPosition);
            yPosition += 5;
          }
          break;
        }
        case MESSAGE_CONTENT_TYPES.AUDIO:
        case MESSAGE_CONTENT_TYPES.DOCUMENT:
        case MESSAGE_CONTENT_TYPES.VIDEO: {
          const contentData = getContentData(chat, t);
          if (contentData) {
            // Check for page break before adding content
            yPosition = checkPageBreak(pdf, yPosition, 10, pageHeight, margin, footerMargin);

            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 255);
            pdf.textWithLink(contentData.contentUrlText, margin, yPosition, {
              url: contentData.contentUrl,
            });
            yPosition += 5;
          }
          break;
        }
        case MESSAGE_CONTENT_TYPES.INTERACTIVE: {
          if (chat.interactive?.kind === MESSAGE_CONTENT_TYPES.BUTTON && chat.interactive.button) {
            // Check if enough space is available for at least the button text
            yPosition = checkPageBreak(pdf, yPosition, 30, pageHeight, margin, footerMargin);

            // Extract links from text and make them clickable
            const buttonText = replaceEmojis(chat.interactive.button.body);
            pdf.setTextColor(50, 50, 50);

            // Interactive button text - use our page break aware renderer
            yPosition = renderTextWithPageBreaks(
              pdf,
              buttonText,
              margin,
              yPosition,
              pageWidth,
              pageHeight,
              margin,
              footerMargin
            );

            // Add extra spacing
            yPosition += 5;

            // Check for page break before options
            yPosition = checkPageBreak(pdf, yPosition, 20, pageHeight, margin, footerMargin);

            // Add button options
            pdf.setFontSize(8);
            pdf.setTextColor(0, 67, 166);
            pdf.text(`${t('options')}:`, margin, yPosition);
            yPosition += 6;
            // Process each button action, with page break checks
            for (const action of chat.interactive.button.actions) {
              // Check for page break before each option
              yPosition = checkPageBreak(pdf, yPosition, 10, pageHeight, margin, footerMargin);

              pdf.setTextColor(50, 50, 50);
              yPosition = renderTextWithPageBreaks(
                pdf,
                `• ${action.title}`,
                margin + 5,
                yPosition,
                pageWidth,
                pageHeight,
                margin,
                footerMargin
              );
            }
          } else if (chat.interactive?.kind === MESSAGE_CONTENT_TYPES.USER_REPLY && chat.interactive.userReply) {
            pdf.setTextColor(0, 100, 0);

            yPosition = renderTextWithPageBreaks(
              pdf,
              `${t('selected')}: ${chat.interactive.userReply.title}`,
              margin,
              yPosition,
              pageWidth,
              pageHeight,
              margin,
              footerMargin
            );
          } else if (chat.interactive?.kind === MESSAGE_CONTENT_TYPES.LIST && chat.interactive.list) {
            // Check if enough space is available for at least the list header
            yPosition = checkPageBreak(pdf, yPosition, 30, pageHeight, margin, footerMargin);

            // Handle list interaction - first display the body text
            pdf.setTextColor(50, 50, 50);

            yPosition = renderTextWithPageBreaks(
              pdf,
              chat.interactive.list.body,
              margin,
              yPosition,
              pageWidth,
              pageHeight,
              margin,
              footerMargin
            );

            // Add spacing after list body
            yPosition += 5;
            // Display each section in the list
            for (const section of chat.interactive.list.sections) {
              // Check for page break before section
              yPosition = checkPageBreak(pdf, yPosition, 20, pageHeight, margin, footerMargin);

              // Section title
              pdf.setFontSize(10);
              pdf.setTextColor(0, 67, 166);
              pdf.setFont(font, bold);

              yPosition = renderTextWithPageBreaks(
                pdf,
                section.title,
                margin,
                yPosition,
                pageWidth,
                pageHeight,
                margin,
                footerMargin
              );

              pdf.setFont(font, normal);

              // Section options
              pdf.setFontSize(9);

              for (const option of section.options) {
                // Check for page break before each option
                yPosition = checkPageBreak(pdf, yPosition, 15, pageHeight, margin, footerMargin);

                pdf.setTextColor(50, 50, 50);

                yPosition = renderTextWithPageBreaks(
                  pdf,
                  `• ${option.title}`,
                  margin + 5,
                  yPosition,
                  pageWidth,
                  pageHeight,
                  margin,
                  footerMargin
                );

                // Option description if available
                if (option.description) {
                  // Check for page break before description
                  yPosition = checkPageBreak(pdf, yPosition, 10, pageHeight, margin, footerMargin);

                  pdf.setTextColor(100, 100, 100);
                  pdf.setFontSize(8);

                  yPosition = renderTextWithPageBreaks(
                    pdf,
                    option.description,
                    margin + 10,
                    yPosition,
                    pageWidth,
                    pageHeight,
                    margin,
                    footerMargin
                  );

                  pdf.setFontSize(9);
                  yPosition += 3; // Add a little extra space after descriptions
                }
              }

              // Add spacing between sections
              yPosition += 5;
            }
          }
          break;
        }
        default:
          pdf.text(`[${t('unsupportedMessageType')}: ${chat?.kind}]`, margin, yPosition);
          yPosition += 5;
      }
    } else if (message.kind === MESSAGE_KINDS.STATUS) {
      yPosition = checkPageBreak(pdf, yPosition, 20, pageHeight, margin, footerMargin);

      // Status message
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.setFont(font, italic);

      let statusText = '';
      if (
        message?.status?.kind === MESSAGES_STATUS.DEPARTMENT_TRANSFER &&
        message.status[MESSAGES_STATUS.DEPARTMENT_TRANSFER]
      ) {
        const transfer = message.status[MESSAGES_STATUS.DEPARTMENT_TRANSFER];
        statusText = `${transfer.transferedBy.name} ${t('transferFrom')} ${transfer.previousDepartment.name} ${t(
          'to'
        )} ${transfer.nextDepartment.name}`;
      } else {
        statusText = message?.status?.message || `${t('status')}: ${message?.status?.kind}`;
      }

      // Render status with page break support
      yPosition = renderTextWithPageBreaks(
        pdf,
        statusText,
        margin,
        yPosition,
        pageWidth,
        pageHeight,
        margin,
        footerMargin
      );

      // Add extra spacing
      yPosition += 5;
      pdf.setFont(font, normal);
    }

    // Add some spacing between messages
    yPosition += 5;

    // Check if we need a new page for the next message
    yPosition = checkPageBreak(pdf, yPosition, 20, pageHeight, margin, footerMargin);
  }
  pdf.save(`${title.toLowerCase().replace(/\s+/g, '-')}-${new Date().getTime()}.pdf`);
}
